import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAdminNavigation } from '@/contexts/AdminNavigationContext';
import {
  type AdminTab,
  businessTabKeys,
  propertyTabKeys,
} from '@/constants/adminTabs';

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import SidebarItem from '@/components/v2/atoms/admin/layout/SidebarItem';
import { LogOut } from 'lucide-react';
import { usePendingTasksCount } from '@/hooks/admin/AdminTodosTabs/useTasks';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const AdminSidebar = () => {
  const { tabs, activeTab, setActiveTab, isSidebarExpanded, setSidebarExpanded } = useAdminNavigation();
  const { data: pendingTasksCount } = usePendingTasksCount();
  const { user, signOut } = useAuth();

  const handleSignOut = async () => {
    await signOut();
  };

  const fullName = `${user?.user_metadata?.first_name ?? ''} ${
    user?.user_metadata?.last_name ?? ''
  }`.trim();

  const email = user?.email ?? '';

  const initials = fullName
    ? `${user?.user_metadata?.first_name?.[0] ?? ''}${
        user?.user_metadata?.last_name?.[0] ?? ''
      }`
    : 'U';

  const propertyTabs = tabs?.filter(
    (tab) => propertyTabKeys.includes(tab.key) && tab.key !== 'todos'
  ) ?? [];

  const businessTabs = tabs?.filter((tab) => businessTabKeys.includes(tab.key) && tab.key !== 'todos') ?? [];
  const todosTab = tabs?.find((tab) => tab.key === 'todos');

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-screen bg-background border-r z-40 transition-all duration-300 ease-in-out flex flex-col',
        isSidebarExpanded ? 'w-64' : 'w-14'
      )}
    >
      {/* Header con Logo */}
      <div className="h-16 flex items-center justify-center border-b shrink-0 overflow-hidden px-3">
        <Button
          variant="ghost"
          className="p-0 h-auto hover:no-underline text-lg font-bold text-foreground"
          onClick={() => setActiveTab?.('dashboard')}
        >
          {isSidebarExpanded ? 'Estancia Corta' : 'EC'}
        </Button>
      </div>

      {/* Contenido scrolleable */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {/* Sección Propiedades */}
        <div className="py-2">
          {isSidebarExpanded && (
            <span className="text-xs font-medium text-muted-foreground px-4 mb-2 block uppercase tracking-wide">
              Propiedades
            </span>
          )}
          {propertyTabs.map((tab) => (
            <SidebarItem
              key={tab.key}
              tab={tab}
              isActive={activeTab === tab.key}
              isExpanded={isSidebarExpanded ?? false}
              onClick={() => setActiveTab?.(tab.key)}
            />
          ))}
        </div>

        <Separator className="my-2" />

        {/* Sección Negocio */}
        <div className="py-2">
          {isSidebarExpanded && (
            <span className="text-xs font-medium text-muted-foreground px-4 mb-2 block uppercase tracking-wide">
              Negocio
            </span>
          )}
          
          {/* Tab de Tareas con badge */}
          {todosTab && (
            <Button
              variant="ghost"
              onClick={() => setActiveTab?.('todos')}
          className={cn(
            'w-full justify-start gap-3 px-3 py-1.5 h-auto transition-colors relative',
            isSidebarExpanded ? 'min-h-[40px]' : 'justify-center min-h-[36px]',
            activeTab === 'todos' && 'bg-muted text-primary font-medium',
            activeTab !== 'todos' && 'hover:bg-muted/50'
          )}
            >
              {/* Indicador de tab activo */}
              {activeTab === 'todos' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-full" />
              )}

              {/* Icono con badge cuando colapsado */}
              <div className={cn('h-5 w-5 shrink-0 flex items-center justify-center relative', activeTab === 'todos' && 'text-primary')}>
                {todosTab.icon}
                {!isSidebarExpanded && pendingTasksCount !== undefined && pendingTasksCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-2 -right-2 h-4 min-w-4 px-0 text-[10px] flex items-center justify-center rounded-full"
                  >
                    {pendingTasksCount > 9 ? '9+' : pendingTasksCount}
                  </Badge>
                )}
              </div>

              {/* Texto con badge cuando expandido */}
              {isSidebarExpanded && (
                <div className="flex items-center justify-between flex-1 min-w-0">
                  <span className="text-sm font-medium truncate">
                    Tareas
                  </span>
                  {pendingTasksCount !== undefined && pendingTasksCount > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="ml-2 h-5 min-w-5 px-0 text-xs flex items-center justify-center rounded-full shrink-0"
                    >
                      {pendingTasksCount > 9 ? '9+' : pendingTasksCount}
                    </Badge>
                  )}
                </div>
              )}
            </Button>
          )}
          
          {businessTabs.map((tab) => (
            <SidebarItem
              key={tab.key}
              tab={tab}
              isActive={activeTab === tab.key}
              isExpanded={isSidebarExpanded ?? false}
              onClick={() => setActiveTab?.(tab.key)}
            />
          ))}
        </div>
      </div>

      {/* Footer con usuario - fuera del área scrolleable */}
      <div className="border-t shrink-0 p-2">
        {isSidebarExpanded ? (
          // Expandido: Mostrar email y botón cerrar sesión directamente
          <div className="flex flex-col gap-2 px-2">
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8 border">
                <AvatarFallback className="bg-blue-50 text-brand-primary text-xs">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-muted-foreground truncate flex-1">
                {email}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 text-destructive hover:text-destructive"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </Button>
          </div>
        ) : (
          // Colapsado: Mostrar avatar con dropdown
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Avatar className="h-8 w-8 border cursor-pointer hover:scale-105 transition-all mx-auto">
                <AvatarFallback className="bg-blue-50 text-brand-primary text-xs">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end" className="w-64 rounded-xl shadow-lg p-2">
              <DropdownMenuItem className="flex flex-col items-start gap-1 cursor-default focus:bg-transparent">
                <p className="text-md font-medium leading-none">{fullName}</p>
                <p className="text-sm text-muted-foreground truncate">{email}</p>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleSignOut}
                className="focus:bg-transparent gap-2 text-destructive focus:text-primary group cursor-pointer"
              >
                <LogOut className="w-4 h-4 group-focus:text-primary" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </aside>
  );
};

export default AdminSidebar;
